/**
 * Shared UI atoms for the HR pages (Directory, Org chart).
 *
 * All styling uses amp-* Tailwind tokens. See COCKPIT.md section 2.9.
 */

import type { ReactNode } from 'react';

export function HrHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between mb-6 gap-4">
      <div className="min-w-0 flex-1">
        <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">
          HR
        </p>
        <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">{title}</h1>
        {subtitle && (
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

export function NotAvailableCard({ what }: { what: string }) {
  return (
    <div className="rounded-lg border border-dashed border-amp-line bg-amp-cream-warm px-4 py-6 text-center dark:border-amp-dark-line dark:bg-amp-dark-surface-warm">
      <p className="text-sm font-medium text-amp-ink dark:text-amp-dark-ink">
        {what} is not available yet
      </p>
      <p className="mt-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
        The HR directory backend is still being set up. It will appear here once it is live.
      </p>
    </div>
  );
}

export function AccessDeniedCard() {
  return (
    <div className="max-w-xl rounded-lg border border-amp-line p-5 dark:border-amp-dark-line">
      <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">No access</h2>
      <p className="text-sm text-amp-ink dark:text-amp-dark-ink">
        You do not have permission to view this page.
      </p>
    </div>
  );
}

export function ErrorCard({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-amp-line bg-amp-white px-4 py-6 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <p className="text-sm text-red-700 dark:text-red-300">Could not load: {message}</p>
    </div>
  );
}

export const inputCls =
  'w-full border border-amp-line rounded px-2.5 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted';

export const primaryBtnCls =
  'inline-flex items-center gap-1 text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1.5 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface';

export const secondaryBtnCls =
  'inline-flex items-center gap-1 text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm';
